import React, { forwardRef, useEffect, useState } from 'react';
import { get, isNil } from 'lodash';
import { useFormContext, Controller } from 'react-hook-form';
import IconMC from '../../../components/IconMC';

const InputRefMC = forwardRef(({
  label,
  placeholder,
  className,
  id,
  onChange,
  onInput,
  value,
  name,
  secondaryLabel,
  maxLength,
  type = 'text',
  textarea = false,
  rows = 3,
  useFloatingLabel = false,
  useFloatingAndNonFloatingLabel = false,
  containerClass = '',
  customError,
  icon = null,
  iconSize = 'base',
  hideBorder = false,
  disabled = false,
  showPasswordToggle = false,
  floatingLabelClassName = '',
  onKeyDown,
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordType = type === 'password';
  const currentType = isPasswordType && showPasswordToggle && showPassword ? 'text' : type;
  const {
    control,
    formState: { errors },
    trigger,
    setValue
  } = useFormContext();
  const onChangeValue = (event, fieldChange) => {
    if (disabled) return;

    fieldChange(event);
    if (onChange) {
      onChange(id || name, event.target.value);
    }
    if (errors[name]) {
      trigger(name);
    }
  };

  const onInputValue = (event, fieldChange) => {
    if (disabled) return;

    fieldChange(event);
    if (onInput) {
      onInput(event);
    }
    if (errors[name]) {
      trigger(name);
    }
  };

  const showLabel = (!useFloatingLabel || useFloatingAndNonFloatingLabel) && label;
  const showFloatingLabel = useFloatingLabel || useFloatingAndNonFloatingLabel;

  const inputColorTypeClassNames = [
    'tw-px-2',
    'tw-py-1',
    'tw-bg-white',
    'tw-rounded-lg',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-blue-400',
    'tw-text-sm',
    'tw-border',
    'tw-w-full',
    'tw-h-[42px]',
    className || ''
  ].join(' ');

  // Determinar padding derecho
  const getRightPadding = () => {
    if (showPasswordToggle && isPasswordType) return 'tw-pr-12';
    if (icon) return 'tw-pr-4';
    return 'tw-pr-4';
  };

  const getLeftPadding = () => {
    if (icon) return 'tw-pl-12';
    if (hideBorder) return 'tw-pl-1';
    return 'tw-pl-4';
  };

  const inputClassNames = [
    'tw-block',
    'tw-rounded-xl',
    getLeftPadding(),
    getRightPadding(),
    showFloatingLabel ? 'tw-pt-5 tw-pb-0' : 'tw-py-3',
    'tw-w-full',
    'tw-text-default',
    'tw-bg-gray-50',
    'tw-border-gray-200',
    'tw-appearance-none',
    'focus:tw-outline-none',
    'focus:tw-ring-2',
    'focus:tw-ring-blue-500',
    'peer',
    hideBorder ? '' : 'tw-border',
    textarea ? 'tw-placeholder-blue-500' : '',
    className || ''
  ].join(' ');

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-text-xs',
    'tw-text-blue-500',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-start-4',
    floatingLabelClassName
  ].filter(Boolean).join(' ');

  useEffect(() => {
    if (!isNil(value)) {
      setValue(name);
    }
  }, [value, setValue, name]);

  const isColorType = type === 'color';

  const labelClasses = 'tw-text-zinc-700 tw-mb-1 tw-text-base tw-font-bold';
  const colorLabelClasses = 'tw-text-xs tw-text-blue-500 tw-mb-0.5';
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field }) => {
        const inputProps = {
          className: isColorType ? inputColorTypeClassNames : inputClassNames,
          placeholder,
          id: id || name,
          type: currentType,
          maxLength,
          onChange: (e) => onChangeValue(e, field.onChange),
          onInput: (e) => onInputValue(e, field.onChange),
          onKeyDown: (e) => {
            if (onKeyDown) {
              onKeyDown(e);
            }
          },
          value: field.value || value
        };

        return (
          <div className={`tw-wrapper ${containerClass}`}>
            {showLabel && (
              <label htmlFor={label} className={isColorType ? colorLabelClasses : labelClasses}>
                {label}
              </label>
            )}
            <div className="tw-relative">
              {icon && (
                <div className="tw-absolute tw-left-4 tw-top-8 tw-transform tw--translate-y-1/2 tw-z-10 tw-pointer-events-none">
                  <IconMC name={icon} size={iconSize} className="tw-text-gray-400" />
                </div>
              )}
              {textarea ? (
                <textarea {...field} {...inputProps} rows={rows} disabled={disabled} ref={ref} />
              ) : (
                <input {...field} {...inputProps} disabled={disabled} ref={ref} />
              )}
              {showFloatingLabel && (
                <label htmlFor={label} className={floatingLabelClassNames}>
                  {secondaryLabel || label}
                </label>
              )}
              {showPasswordToggle && isPasswordType && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-pointer tw-z-10 tw-bg-transparent tw-border-none tw-p-0"
                  tabIndex={-1}
                >
                  <IconMC name={showPassword ? 'eye-outline' : 'eye-fill'} size="base" />
                </button>
              )}
            </div>
            {get(errors, name) && <p className="tw-text-xs tw-text-red-500">{get(errors, name).message}</p>}
            {customError && <p className="tw-text-xs tw-text-red-500">{customError}</p>}
          </div>
        );
      }}
    />
  );
});

export default InputRefMC;
